import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useData, useNotifs } from '../contexts/index';
import { useActiveHouse } from '../contexts/ActiveHouseContext';
import { useOffline } from '../contexts/OfflineContext';
import { useDataSync } from './useDataSync';
import { usePresentAppError } from './usePresentAppError';
import { normalizeRouteStop, type RouteStop } from '../core/models/RouteStop';
import { isRouteDepartureAuthorized } from '../core/rules/routeDeparture';
import {
  canAuthorizeDeparture,
  canMarkRouteCompletedManually,
  getRouteCollectionStatus,
} from '../core/rules/routeCollection';
import { sortStopsBySequence } from '../core/rules/routeOptimize';
import {
  canReceiveRouteAssignments,
  isServiceAgreementEnabled,
} from '../core/rules/runnerAssignment';
import {
  confirmHouseRoute,
  draftHouseRoute,
} from '../services/firebase/FunctionsService';

type T = Record<string, string>;

/** Shared route data, runner assignment, and auto draft/confirm lifecycle. */
export function useBankerRouteCompose(t: T) {
  const { user } = useAuth();
  const { adapter } = useData();
  const { activeHouseId } = useActiveHouse();
  const { push } = useNotifs();
  const presentAppError = usePresentAppError();
  const { online } = useOffline();
  const houseId = String(activeHouseId || user?.houseId || '').trim();
  const raw = useDataSync(adapter, 'routeStops', []) as RouteStop[];
  const activeRouteRows = useDataSync(adapter, 'activeRoute', []) as Array<Record<string, unknown>>;
  const users = useDataSync(adapter, 'users', []) as Array<Record<string, unknown>>;
  const activeRoute = activeRouteRows[0] ?? null;
  // Seeded/legacy routes have no compositionStatus — treat as confirmed once they exist.
  const compositionStatus = activeRoute
    ? String(activeRoute.compositionStatus ?? 'confirmed')
    : 'none';
  const isConfirmed = compositionStatus === 'confirmed' || compositionStatus === 'active';
  const routeId = String(activeRoute?.id ?? '');
  const allRunners = useMemo(
    () => users.filter((u) => String(u.role) === 'runner' && String(u.status ?? 'active') === 'active'),
    [users],
  );
  const eligibleRunners = useMemo(
    () => allRunners.filter((u) => canReceiveRouteAssignments(u)),
    [allRunners],
  );
  const runners = eligibleRunners.length > 0 ? eligibleRunners : allRunners;
  const singleRunner = eligibleRunners.length === 1;
  const [localStops, setLocalStops] = useState<Record<string, RouteStop>>({});
  const [removedIds, setRemovedIds] = useState<Record<string, true>>({});
  const stops = useMemo(
    () => sortStopsBySequence(raw
      .filter((r) => !removedIds[String(r.id)])
      .map((r) => {
        const n = normalizeRouteStop(r);
        return localStops[n.id] ? { ...n, ...localStops[n.id] } : n;
      })),
    [raw, localStops, removedIds],
  );
  const [draftBusy, setDraftBusy] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [runnerId, setRunnerId] = useState('');
  const autoDraftKey = useRef('');
  const autoAssignKey = useRef('');
  const lastAssignedRunner = useRef('');
  const selfCollect = eligibleRunners.length === 0
    || activeRoute?.selfCollect === true
    || String(activeRoute?.collectorRole ?? '') === 'banker';

  useEffect(() => {
    if (runnerId) return;
    const preferred = String(activeRoute?.assignedRunnerId ?? '');
    if (preferred && runners.some((r) => String(r.id) === preferred)) {
      setRunnerId(preferred);
      return;
    }
    const firstEligible = eligibleRunners[0] ?? runners[0];
    if (firstEligible) setRunnerId(String(firstEligible.id));
  }, [runnerId, runners, eligibleRunners, activeRoute?.assignedRunnerId]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('banker-route-compose');
    if (!el) return;
    const onHash = () => {
      if (window.location.hash === '#banker-route-compose') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!online || !houseId || draftBusy) return;
    if (compositionStatus !== 'none') return;
    if (selfCollect) {
      const key = `${houseId}:self`;
      if (autoDraftKey.current === key) return;
      autoDraftKey.current = key;
      setDraftBusy(true);
      void draftHouseRoute({ houseId, selfCollect: true })
        .catch((err) => {
          autoDraftKey.current = '';
          const { copy } = presentAppError(err, { t, push: undefined });
          push({
            cat: 'routes',
            sev: 'danger',
            title: copy?.title || t.routeDraftFailed || t.err_generic,
            body: copy?.hint || t.err_generic_hint || '',
          });
        })
        .finally(() => setDraftBusy(false));
      return;
    }
    if (!runnerId) return;
    const selected = runners.find((r) => String(r.id) === runnerId);
    if (!selected || !canReceiveRouteAssignments(selected)) return;
    const key = `${houseId}:${runnerId}`;
    if (autoDraftKey.current === key) return;
    autoDraftKey.current = key;
    setDraftBusy(true);
    void draftHouseRoute({ houseId, runnerId })
      .catch((err) => {
        autoDraftKey.current = '';
        const { copy } = presentAppError(err, { t, push: undefined });
        push({
          cat: 'routes',
          sev: 'danger',
          title: copy?.title || t.routeDraftFailed || t.err_generic,
          body: copy?.hint || t.err_generic_hint || '',
        });
      })
      .finally(() => setDraftBusy(false));
  }, [
    online, houseId, runnerId, compositionStatus, draftBusy, runners, selfCollect,
    presentAppError, push, t,
  ]);

  const assignRoute = async (silent = false) => {
    if (confirmBusy || !online || !routeId) return false;
    if (!selfCollect && !runnerId) return false;
    if (!selfCollect) {
      const selected = runners.find((r) => String(r.id) === runnerId);
      if (selected && !canReceiveRouteAssignments(selected)) {
        if (!silent) {
          push({
            cat: 'routes',
            sev: 'warn',
            title: t.routeConfirmFailed ?? t.err_generic,
            body: !isServiceAgreementEnabled(selected)
              ? (t.agreementEnableAssignments
                ?? 'Enable the runner service agreement before assigning routes.')
              : (t.runnerInactive ?? 'Runner must be active.'),
          });
        }
        return false;
      }
    }
    setConfirmBusy(true);
    try {
      await confirmHouseRoute({
        houseId,
        routeId,
        ...(selfCollect
          ? { selfCollect: true }
          : { runnerId: runnerId || activeRoute?.assignedRunnerId }),
      });
      lastAssignedRunner.current = selfCollect ? String(user?.id ?? '') : runnerId;
      if (!silent) {
        push({
          cat: 'routes',
          sev: 'success',
          title: t.routeAssigned ?? t.routeConfirmed ?? 'Route configured',
          body: selfCollect
            ? (t.routeSelfCollectAssignedBody ?? t.routeAssignedBody ?? '')
            : (t.routeAssignedBody ?? t.routeConfirmedBody ?? ''),
        });
      }
      return true;
    } catch (err) {
      const { copy } = presentAppError(err, { t });
      push({
        cat: 'routes',
        sev: 'danger',
        title: copy?.title || t.routeConfirmFailed || t.err_generic,
        body: copy?.hint || '',
      });
      return false;
    } finally {
      setConfirmBusy(false);
    }
  };

  // Lock configuration without a second confirm CTA — only Authorize sends to runner.
  useEffect(() => {
    if (!online || !houseId || !routeId || confirmBusy || draftBusy) return;
    if (compositionStatus !== 'draft') return;
    if (selfCollect) {
      const key = `${routeId}:self`;
      if (autoAssignKey.current === key) return;
      autoAssignKey.current = key;
      void assignRoute(true).then((ok) => {
        if (!ok) autoAssignKey.current = '';
      });
      return;
    }
    if (!runnerId) return;
    const selected = runners.find((r) => String(r.id) === runnerId);
    if (!selected || !canReceiveRouteAssignments(selected)) return;
    const key = `${routeId}:${runnerId}`;
    if (autoAssignKey.current === key) return;
    autoAssignKey.current = key;
    void assignRoute(true).then((ok) => {
      if (!ok) autoAssignKey.current = '';
    });
  // assignRoute closes over latest state; key guards re-entry.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, houseId, runnerId, routeId, compositionStatus, confirmBusy, draftBusy, runners, selfCollect]);

  const departureOk = isRouteDepartureAuthorized(activeRoute);
  const collectionStatus = getRouteCollectionStatus(activeRoute);
  const collectionLocked = collectionStatus === 'in_progress';
  const authorizeGate = canAuthorizeDeparture({
    ...activeRoute,
    compositionStatus,
  }, stops);
  const canAuthorizeNow = authorizeGate.ok;
  const authorizeBlockedReason = authorizeGate.ok ? null : authorizeGate.reason;
  const canCompleteNow = canMarkRouteCompletedManually({
    ...activeRoute,
    compositionStatus,
  }).ok;

  const onRunnerChange = (nextId: string) => {
    setRunnerId(nextId);
    if (!nextId || !isConfirmed || collectionLocked) return;
    void (async () => {
      const prev = lastAssignedRunner.current;
      lastAssignedRunner.current = '';
      autoAssignKey.current = '';
      const ok = await assignRoute(false);
      if (!ok) {
        setRunnerId(prev || String(activeRoute?.assignedRunnerId ?? ''));
      }
    })();
  };

  return {
    user,
    adapter,
    online,
    houseId,
    activeRoute,
    compositionStatus,
    isConfirmed,
    routeId,
    allRunners,
    eligibleRunners,
    runners,
    singleRunner,
    stops,
    setLocalStops,
    setRemovedIds,
    draftBusy,
    confirmBusy,
    runnerId,
    selfCollect,
    departureOk,
    collectionStatus,
    collectionLocked,
    canAuthorizeNow,
    authorizeBlockedReason,
    canCompleteNow,
    onRunnerChange,
  };
}
